import { useApp } from "@/AppProvider"
import { API_URL } from "@/constants"
import { useGetSubscription } from "@/hooks/useSubscription"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import { useParams, useSearchParams } from "react-router-dom"
import { Spinner } from "../common"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"

export const BillingSettings = () => {
  const { token, addToast } = useApp()
  const { organizationId } = useParams()
  const [search] = useSearchParams()

  const { data, isLoading, isError, error } = useGetSubscription(token, organizationId)

  if (isLoading) {
    return (
      <div className="card p-4">
        <div className="flex items-center">
          <Spinner /> Loading…
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="card p-4">
        <ErrorMessage message={error.message} />
      </div>
    )
  }

  const subscription = data?.subscription

  return (
    <div className="card p-4">
      <h2 className="text-lg font-semibold text-gray-900">Billing settings</h2>
      <p className="text-sm text-gray-600">Manage your subscription and billing information.</p>

      <div className="mt-4">
        {search.get("success") === "true" && search.get("session_id") ? (
          <div className="mt-4 flex w-fit items-center gap-x-2 border-l-2 border-green-500 bg-green-50 py-2.5 pr-4 pl-3 font-medium text-green-600">
            <CheckBadgeIcon className="h-5 w-5" aria-hidden="true" />
            Thanks for upgrading! You can edit your billing information anytime below.
          </div>
        ) : search.get("success") === "false" ? (
          <ErrorMessage message="There was an error processing your subscription or payment." />
        ) : null}
      </div>

      <div className="mt-4">
        {!!subscription ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Lab reports downloaded this month:{" "}
              <span className="font-semibold">{data?.labReportsDownloaded ?? 0}/500</span>
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault()

                // Redirect to the billing portal where customers can manage their
                // subscription, update payment methods, and view billing history.
                const res = await fetch(`${API_URL}/organizations/${organizationId}/billing/portal`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({}),
                })
                const data = await res.json()
                if (!res.ok || !data?.url) {
                  addToast("Error", "Failed to connect to billing service", "error")
                  return
                }

                window.location.href = data.url
              }}
            >
              <button id="checkout-and-portal-button" type="submit" className="btn">
                Mange your billing information
              </button>
            </form>
          </div>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault()

              // Redirect to the Stripe hosted checkout page to set up a new subscription.
              const res = await fetch(`${API_URL}/organizations/${organizationId}/billing/checkout`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({}),
              })
              const data = await res.json()
              if (!res.ok || !data?.url) {
                addToast("Error", "Failed to connect to billing service", "error")
                return
              }

              window.location.href = data.url
            }}
          >
            <button id="checkout-and-portal-button" type="submit" className="btn">
              Set up subscription
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
